import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  CustomSectionTitle,
  CustomButton,
  CustomTable,
  CustomSelect,
  CustomLoading,
  MobileTable,
} from "@ippon/shared-components";
import type { TableProps } from "@ippon/shared-components";
import { ExternalLinkIcon } from "@ippon/shared-assets";
import { getClientID } from "@/utils/authUtils";
import { fiscalYear, yen } from "./billingFormat";
import { InvoiceSummary } from "./types";
import { fetchInvoicesApi } from "./billingApi";
import { openInvoicePdf } from "./invoicePdf";

// ===== カラー（既存 利用状況画面 AiUsageSetting と統一） =====
const MUTED = "#6B7280";
const BORDER = "1px solid #E0E0E0";
const INK = "#354053";

// 状態（請求済 / 入金済）は入金実績のデータが無く一律「請求済」になるため、
// 誤解を招かないよう列ごと非表示にしている。Payments 連携時に復帰させる。

const BillingList: React.FC = () => {
  // スマートフォンでは列が収まらず横に見切れるため、カード表示に切り替える。
  // 判定は物件管理の一覧（見積書一覧・物件構成一覧）と同じ sm 未満。
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 遡れる最大月数をまとめて取得し、年度の絞り込みは取得済みデータで行う
  // （年度を切り替えても再取得しない）。
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);

  const clientId = getClientID();

  const loadInvoices = useCallback(async () => {
    if (!clientId) {
      setError("クライアント情報を取得できませんでした。再度ログインしてください。");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const items = await fetchInvoicesApi(clientId);
      setInvoices(items);
    } catch {
      setInvoices([]);
      setError("請求情報を取得できませんでした。時間をおいて再度お試しください。");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // 年度の選択肢（取得済みの請求から作る・新しい順）
  const years = useMemo(
    () =>
      Array.from(
        new Set(invoices.map((i) => fiscalYear(i.year, i.month)))
      ).sort((a, b) => b - a),
    [invoices]
  );

  // 再取得で選択中の年度が無くなったら最新年度に追従する
  useEffect(() => {
    if (years.length === 0) {
      setYear(null);
      return;
    }
    setYear((prev) => (prev != null && years.includes(prev) ? prev : years[0]));
  }, [years]);

  const yearOptions = useMemo(
    () => years.map((y) => ({ value: String(y), label: `${y}年度` })),
    [years]
  );

  const rows = useMemo(
    () =>
      year == null
        ? invoices
        : invoices.filter((i) => fiscalYear(i.year, i.month) === year),
    [invoices, year]
  );

  const columns: TableProps["columns"] = useMemo(
    () => [
      {
        field: "period",
        headerName: "請求月",
        flex: 0.9,
        minWidth: 110,
        renderCell: ({ row }: { row: InvoiceSummary }) => (
          <Box
            data-testid="billing-row-period"
            data-invoice-id={row.id}
            sx={{ color: INK, fontSize: 14, fontWeight: 700 }}
          >
            {row.year}年{row.month}月
          </Box>
        ),
      },
      {
        field: "total",
        headerName: "合計金額（税込）",
        flex: 1.1,
        minWidth: 140,
        renderCell: ({ row }: { row: InvoiceSummary }) => (
          <Box
            data-testid="billing-row-total"
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: INK,
              width: "100%",
            }}
          >
            {yen(row.total)}
          </Box>
        ),
      },
      {
        field: "issueDate",
        headerName: "発行日",
        flex: 0.9,
        minWidth: 110,
        renderCell: ({ row }: { row: InvoiceSummary }) => (
          <Box sx={{ fontSize: 13, color: INK }}>{row.issueDate}</Box>
        ),
      },
      {
        field: "dueDate",
        headerName: "支払期限",
        flex: 0.9,
        minWidth: 110,
        renderCell: ({ row }: { row: InvoiceSummary }) => (
          <Box sx={{ fontSize: 13, color: INK }}>{row.dueDate}</Box>
        ),
      },
      {
        field: "invoiceNo",
        headerName: "請求書番号",
        flex: 1.2,
        minWidth: 150,
        renderCell: ({ row }: { row: InvoiceSummary }) => (
          <Box data-testid="billing-row-invoice-no" sx={{ fontSize: 13, color: INK }}>
            {row.invoiceNo}
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "操作",
        // ボタン1つ分あれば足りるため、ここだけ固定幅にして
        // 余った幅は他の列へ配分する
        width: 150,
        sortable: false,
        renderCell: ({ row }: { row: InvoiceSummary }) =>
          // 請求書PDFが未作成の月はボタンを出さない（月次バッチ生成待ち）
          row.hasPdf === false ? null : (
            <Box
              data-testid="billing-pdf-button"
              data-invoice-id={row.id}
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                width: "100%",
              }}
            >
              <CustomButton
                label="PDF表示"
                variant="contained"
                sx={{
                  flexShrink: 0,
                  // MUI の既定は 8px。文字とアイコンを詰める
                  "& .MuiButton-endIcon": { marginLeft: "2px" },
                }}
                endIcon={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      "& svg": { width: 25, height: 25 },
                    }}
                  >
                    <ExternalLinkIcon color="#ffffff" />
                  </Box>
                }
                onClick={() => openInvoicePdf(row)}
              />
            </Box>
          ),
      },
    ],
    []
  );

  // カード表示用の列。
  // 請求月はカードの見出しに出すため明細からは外す。金額は幅いっぱいの
  // 右寄せだとラベルから離れて読みにくいため、左寄せに上書きする。
  const mobileColumns: TableProps["columns"] = useMemo(
    () =>
      columns
        .filter((column) => column.field !== "period")
        .map((column) =>
          column.field === "total"
            ? {
                ...column,
                renderCell: ({ row }: { row: InvoiceSummary }) => (
                  <Box
                    data-testid="billing-row-total"
                    sx={{ fontSize: 15, fontWeight: 700, color: INK }}
                  >
                    {yen(row.total)}
                  </Box>
                ),
              }
            : column
        ),
    [columns]
  );

  return (
    <Box
      pt={{ xs: 2, sm: 3 }}
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <CustomSectionTitle title="請求一覧" addBorder size="large" />

      {/* タブを廃止したため、グレーの帯は敷かずページ背景のまま並べる */}
      <Box
        sx={{
          pt: 1.7,
          pb: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 年フィルタ */}
          <Box sx={{ display: "flex", alignItems: "center", minHeight: "34px" }}>
            {yearOptions.length > 0 && year != null && (
              <CustomSelect
                key={`year-${year}`}
                name="year"
                label=""
                options={yearOptions}
                defaultValue={String(year)}
                onChange={(v: string) => setYear(Number(v))}
                width="120px"
                XsWidth="110px"
              />
            )}
          </Box>

          {/* 請求一覧テーブル */}
          <Box sx={{ mt: "16px", position: "relative" }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 160,
                }}
              >
                <CustomLoading loading fullScreen={false} size="medium" />
              </Box>
            ) : error ? (
              <Box
                sx={{
                  background: "#fff",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  p: "32px 20px",
                  textAlign: "center",
                  color: MUTED,
                  fontSize: ".9rem",
                }}
              >
                {error}
                <Box sx={{ mt: 2 }}>
                  <CustomButton
                    label="再試行"
                    variant="outlined"
                    onClick={loadInvoices}
                  />
                </Box>
              </Box>
            ) : isMobile ? (
              // MobileTable は空のときに何も描かないため、ここで案内を出す
              rows.length === 0 ? (
                <Box
                  data-testid="billing-no-data"
                  sx={{
                    background: "#fff",
                    border: BORDER,
                    borderRadius: "8px",
                    p: "32px 20px",
                    textAlign: "center",
                    color: MUTED,
                    fontSize: ".9rem",
                  }}
                >
                  請求データがありません
                </Box>
              ) : (
                <MobileTable
                  rows={rows}
                  columns={mobileColumns}
                  hidePagination
                  // 行タップで開く詳細画面は無いため、見出しはリンクにしない
                  disableNameClick
                  // 請求は一括操作を持たないためチェックボックスは出さない
                  // （MobileTable の既定は表示）
                  checkBoxOpen={false}
                  renderTitle={(row: InvoiceSummary) => (
                    <Box
                      data-testid="billing-row-period"
                      data-invoice-id={row.id}
                      sx={{ color: INK, fontSize: 14, fontWeight: 700 }}
                    >
                      {row.year}年{row.month}月
                    </Box>
                  )}
                />
              )
            ) : (
              <CustomTable
                rows={rows}
                columns={columns}
                hidePagination
                noDataMessage="請求データがありません"
              />
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default BillingList;
